import { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { Table } from "@covertree/ui/table/Table";
import { LoadingOverlay } from "@covertree/ui";

import {
  GetQuoteFilterByStatus,
  PolicyStatus,
  RenewalStatus,
  SearchResult as SearchResultGraphQL,
  SearchResultType,
} from "@covertree/agent-portal-graphql";

import { mapSearchResultStatus } from "@components/Search/utils";
import { useContinueQuote } from "@providers/ContinueQuoteProvider";
import { useUWReason } from "@providers/UWReasonProvider/UWReasonProvider";
import { useOpenRenewalDetails } from "@hooks/actions/useOpenRenewalDetails";
import { getOpenRenewalDetailsLabelByRenewalStatus } from "@utils/getOpenRenewalDetailsLabelByRenewalStatus";
import { useOpenPolicyDetails } from "@hooks/actions/useOpenPolicyDetails";
import { useSendEsignReminder } from "@hooks/actions/useSendEsignReminder";
import { SEQUOIA_CONTINUE_PATH } from "@routing/paths";
import { useAccessPattern } from "@hooks/useAccessPattern";
import { featureFlags } from "@utils/featureFlags";

interface Props {
  header: string;
  result?: SearchResultGraphQL[];
}

const getColumns = (isRenewal: boolean) => [
  { headerName: isRenewal ? "Renewal ID" : "Policy ID" },
  { headerName: "Name" },
  { headerName: "Address" },
  { headerName: "Status" },
];

export function SearchResult({ header, result }: Props) {
  if (!result || result.length === 0) return null;

  const navigate = useNavigate();
  const openRenewalDetails = useOpenRenewalDetails();
  const openPolicyDetails = useOpenPolicyDetails();
  const { showContinueQuoteModal } = useContinueQuote();
  const { sendEsignReminder, loading: sendEsignReminderLoading } =
    useSendEsignReminder();
  const { canWriteBusiness: canContinueQuote } = useAccessPattern();
  const { showUWReasonModal } = useUWReason();

  const actionMenuMap = useMemo(
    () => ({
      [SearchResultType.Renewal]: (item: SearchResultGraphQL) => [
        {
          label: "View Policy Details",
          onClick: () => openPolicyDetails(item.policyLocator),
        },
        {
          label: getOpenRenewalDetailsLabelByRenewalStatus(
            item.status as RenewalStatus
          ),
          onClick: () => openRenewalDetails(item.renewalLocator),
        },
      ],

      [SearchResultType.Policy]: (item: SearchResultGraphQL) => [
        {
          label: "View Details",
          onClick: () => openPolicyDetails(item.policyLocator),
        },
        ...(item.status === PolicyStatus.PendingEsignRequired &&
        !(item.isAgentsSignResponsibility || !item.paperlessDiscount)
          ? [
              {
                label: "Send esign reminder",
                onClick: () => sendEsignReminder(item.policyLocator),
              },
            ]
          : []),
      ],

      [SearchResultType.Sequoia]: (item: SearchResultGraphQL) =>
        canContinueQuote && item.quoteStatus === GetQuoteFilterByStatus.Valid
          ? [
              {
                label: "Start book conversion",
                onClick: () =>
                  navigate(`/${SEQUOIA_CONTINUE_PATH}/${item.policyLocator}`, {
                    state: {
                      previousPath: location.pathname,
                    },
                  }),
              },
            ]
          : undefined,

      [SearchResultType.Quote]: (item: SearchResultGraphQL) => {
        const isContinueQuoteAvailable =
          canContinueQuote &&
          [
            GetQuoteFilterByStatus.Valid,
            GetQuoteFilterByStatus.Pending,
          ].includes(item.quoteStatus);

        const isViewReasonAvailable =
          featureFlags.viewUWReasons.isEnabled() &&
          item.quoteStatus === GetQuoteFilterByStatus.Pending;

        return isContinueQuoteAvailable || isViewReasonAvailable
          ? [
              ...(isContinueQuoteAvailable
                ? [
                    {
                      label: "Continue quote",
                      onClick: () => {
                        showContinueQuoteModal(
                          item.policyLocator,
                          item.startDateAfterUWReview
                        );
                      },
                    },
                  ]
                : []),
              ...(isViewReasonAvailable
                ? [
                    {
                      label: "View reason",
                      onClick: () => {
                        showUWReasonModal(item.policyLocator);
                      },
                    },
                  ]
                : []),
            ]
          : undefined;
      },
    }),
    [
      openPolicyDetails,
      openRenewalDetails,
      sendEsignReminder,
      navigate,
      location.pathname,
    ]
  );

  return (
    <Box>
      <Typography variant="h4" pl={1}>
        {header}
      </Typography>
      <Table
        id={`search-result`}
        rows={result.map((item) => ({
          id: `search-result-${item.renewalLocator ?? item.policyLocator}`,
          data: [
            item.renewalLocator ?? item.policyLocator,
            `${item.policyholderFirstName} ${item.policyholderLastName}`,
            [
              item.unitAddress.streetAddress,
              item.unitAddress.city,
              item.unitAddress.state,
              item.unitAddress.zipCode,
            ].join(" "),
            mapSearchResultStatus(item),
          ],
          actionMenuItems: actionMenuMap[item.searchResultType]?.(item),
        }))}
        columns={getColumns(
          result[0].searchResultType === SearchResultType.Renewal
        )}
      />
      {sendEsignReminderLoading && <LoadingOverlay />}
    </Box>
  );
}
